// ═══════════════════════════════════════════════════════════════════════════════
// Receipt list — pairing receipts (employer & helper); filter by status / date range
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RECEIPT_STATUS, type ReceiptListItem } from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import {
  getTodayIsoDateInTimezone,
  ReceiptListHistoryStyleGrouped,
} from '../../components/receipt/ReceiptListHistoryStyleGrouped';
import { receiptApi } from '../../lib/api';
import {
  getFirstDayOfPreviousMonthYmdInTimezone,
  resolveDisplayTimeZone,
} from '../../lib/userTodayYmd';
import { AppPageTitleSection } from '../../components/ui/AppPageTitleSection';
import { StatusFilterChipRow } from '../../components/ui/StatusFilterChipRow';

type StatusTab = 'all' | 'save' | 'submitted' | 'reviewed';

const PAGE_SIZE = 20;

export const ReceiptListPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const timezone = resolveDisplayTimeZone(user?.settings.timezone);
  const receiptListTodayYmd = useMemo(() => getTodayIsoDateInTimezone(timezone), [timezone]);
  const defaultListStartDate = useMemo(
    () => getFirstDayOfPreviousMonthYmdInTimezone(timezone),
    [timezone]
  );

  const [statusTab, setStatusTab] = useState<StatusTab>('all');
  const [startDate, setStartDate] = useState(defaultListStartDate);
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<ReceiptListItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (nextPage: number, append: boolean) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const res = await receiptApi.list({
          status: statusTab,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          page: nextPage,
          pageSize: PAGE_SIZE,
        });
        if (!res.success || !res.data) {
          setError(res.error ?? t('receipt.list.loadError'));
          return;
        }
        const data = res.data;
        const batch = Array.isArray(data) ? data : (data.items ?? []);
        const pages = Array.isArray(data) ? 1 : (data.totalPages ?? 1);
        setTotalPages(pages);
        setPage(nextPage);
        setItems((prev) => (append ? [...prev, ...batch] : batch));
      } catch {
        setError(t('receipt.list.loadError'));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [statusTab, startDate, endDate, t]
  );

  useEffect(() => {
    void fetchPage(1, false);
  }, [statusTab, startDate, endDate, fetchPage]);

  const handleStatusTab = (next: StatusTab): void => {
    setStatusTab(next);
  };

  const handleLoadMore = (): void => {
    if (page >= totalPages || loadingMore) {
      return;
    }
    void fetchPage(page + 1, true);
  };

  const handleLoadMoreKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleLoadMore();
    }
  };

  const formatMoney = (n: number): string => (n < 0 ? `-$${Math.abs(n).toFixed(2)}` : `$${n.toFixed(2)}`);

  const statusFilterOptions = useMemo(
    () =>
      (
        [
          { value: 'all' as const, labelKey: 'receipt.list.filter.all' },
          { value: RECEIPT_STATUS.SAVE, labelKey: 'receipt.list.filter.save' },
          { value: RECEIPT_STATUS.SUBMITTED, labelKey: 'receipt.list.filter.submitted' },
          { value: RECEIPT_STATUS.REVIEWED, labelKey: 'receipt.list.filter.reviewed' },
        ] as const
      ).map((row) => ({ value: row.value as StatusTab, label: t(row.labelKey) })),
    [t]
  );

  return (
    <div className="min-h-full bg-gray-50 px-4 py-6 pb-24 dark:bg-gray-950">
      <div className="mx-auto max-w-lg">
        <div className="mb-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-800"
            aria-label={t('task.action.back')}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
        <AppPageTitleSection title={t('receipt.list.title')} subtitle={t('receipt.list.subtitle')} />

        <StatusFilterChipRow
          className="mb-4"
          ariaLabel={t('receipt.list.filterAria')}
          options={statusFilterOptions}
          value={statusTab}
          onChange={handleStatusTab}
        />

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <IsoDatePicker
            id="receipt-list-start"
            ariaLabel={t('receipt.list.startDate')}
            value={startDate}
            onChange={setStartDate}
            maxDate={endDate || undefined}
            className="min-w-0 flex-1 basis-[140px]"
            valueFormat="iso"
          />
          <span className="shrink-0 px-0.5 text-sm font-medium text-gray-500 dark:text-gray-400" aria-hidden>
            {t('settlement.helperConfirmDateRangeSeparator')}
          </span>
          <IsoDatePicker
            id="receipt-list-end"
            ariaLabel={t('receipt.list.endDate')}
            value={endDate}
            onChange={setEndDate}
            minDate={startDate || undefined}
            className="min-w-0 flex-1 basis-[140px]"
            valueFormat="iso"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('receipt.list.empty')}</p>
        ) : (
          <>
            <ReceiptListHistoryStyleGrouped
              items={items}
              todayYmd={receiptListTodayYmd}
              timezone={timezone}
              language={language}
              t={t}
              formatMoney={formatMoney}
            />
            {page < totalPages ? (
              <button
                type="button"
                onClick={handleLoadMore}
                onKeyDown={handleLoadMoreKeyDown}
                tabIndex={0}
                disabled={loadingMore}
                className="mt-4 w-full min-h-11 rounded-lg border border-gray-200 bg-white py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                aria-label={t('receipt.list.loadMore')}
              >
                {loadingMore ? t('common.loading') : t('receipt.list.loadMore')}
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};
